/**
 * BSS Pipeline Inspector
 * Animated 8-scene replay of any org's latest calculation trace.
 */

import { createFileRoute } from '@tanstack/react-router';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { fetchRoadmapResult, type RoadmapResult } from '@/api/roadmap';
import { lifecycleLabelFor } from '@/lib/prime-score/bands';
import { formatDateTimeShort } from '@/lib/dates';
import { ChevronRight, ChevronLeft, ChevronDown, RotateCcw, Loader2, Gauge, RefreshCw, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useRecalculateBSS } from '@/hooks/use-bss-calculator';

export const Route = createFileRoute('/admin/diagnostic/inspect')({
  component: BSSInspector,
});

// ─── Types ────────────────────────────────────────────────────────────────────

interface Org { id: string; name: string }
interface Calculation {
  id: string;
  session_id: string;
  bss_score: string;
  adjusted_bss_score: string;
  pillar_scores: Record<string, number>;
  lifecycle_stage: string;
  lifecycle_zone: string;
  guardrail_violations: string[];
  trend_modifier: string;
  decline_index: string;
  suggestions: any[];
  is_latest: boolean;
  calculated_at: string;
  calculation_trace: BSSTrace | null;
}

interface BSSTrace {
  steps: {
    responsesLoaded?: {
      totalResponses: number;
      responses: Array<{ code: string; pillar: string; type: string; answerNumeric: number | null; answerValue: string | null; isIdk: boolean }>;
    };
    questionScores?: Array<{ code: string; pillar: string; type: string; weight: number; rawScore: number | null; formula: string }>;
    pillarAggregation?: Array<{ code: string; name: string; pillarWeight: number; score: number; answeredCount: number; totalCount: number; idkCount: number }>;
    rawBSS?: { score: number; contributions: Array<{ code: string; score: number; weight: number; contribution: number }>; weightTotal: number };
    trendModifier?: { trendQuestions: Array<{ code: string; score: number }>; avgTrendScore: number; declineIndex: number; modifier: number; adjustedBSS: number };
    guardrailEvaluation?: Array<{ code: string; name: string; description: string; logicSummary: string; fired: boolean }>;
    lifecycleStage?: { adjustedBSS: number; candidateStages: string[]; selectedStage: string; selectedZone: string; reason: string };
    suggestions?: { candidates: Array<{ code: string; priority: number; pillarCode?: string; violationCode?: string }>; selected: Array<{ code: string; title: string; priority: number }> };
  };
  durationMs: number;
}

interface ScoringRange { min: number; max: number; score: number }
type QuestionRulesMap = Record<string, ScoringRange[] | undefined>

function RangesTooltip({ ranges }: { ranges: ScoringRange[] }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors ml-1">
            <Info className="w-3 h-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          className="bg-popover text-popover-foreground border border-border shadow-md p-0 overflow-hidden"
        >
          <div className="text-[11px]">
            <div className="grid grid-cols-3 gap-x-4 bg-muted/50 px-3 py-1.5 font-medium text-muted-foreground">
              <span>Min</span><span>Max</span><span>Score</span>
            </div>
            {ranges.map((r, i) => (
              <div key={i} className="grid grid-cols-3 gap-x-4 px-3 py-1 border-t border-border font-mono">
                <span>{r.min}</span><span>{r.max}</span><span>{r.score}</span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const SCENE_LABELS = ['Responses', 'Scoring', 'Pillars', 'Raw BSS', 'Trend', 'Guardrails', 'Stage', 'Result'];
const TOTAL_SCENES = 8;
const PILLAR_ORDER = ['culture', 'finance', 'management', 'marketing', 'operations', 'sales'];

// ─── Score colour helpers ─────────────────────────────────────────────────────

function scoreColour(score: number) {
  if (score >= 70) return 'bg-emerald-500';
  if (score >= 40) return 'bg-amber-400';
  return 'bg-red-500';
}

function scoreBg(score: number) {
  if (score >= 70) return 'bg-emerald-500/10 text-emerald-600';
  if (score >= 40) return 'bg-amber-400/10 text-amber-600';
  return 'bg-red-500/10 text-red-600';
}

// ─── Animated score bar ───────────────────────────────────────────────────────

function ScoreBar({ value, animate }: { value: number; animate: boolean }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!animate) { setWidth(value); return; }
    setWidth(0);
    const t = setTimeout(() => setWidth(value), 50);
    return () => clearTimeout(t);
  }, [value, animate]);
  return (
    <div className="h-1.5 bg-muted rounded-full overflow-hidden w-full">
      <div className={`h-full rounded-full transition-all duration-700 ease-out ${scoreColour(value)}`} style={{ width: `${width}%` }} />
    </div>
  );
}

// ─── Counting number animation ────────────────────────────────────────────────

function CountUp({ target, animate, decimals = 1 }: { target: number; animate: boolean; decimals?: number }) {
  const [val, setVal] = useState(animate ? 0 : target);
  useEffect(() => {
    if (!animate) { setVal(target); return; }
    setVal(0);
    const steps = 40;
    const stepMs = 600 / steps;
    let i = 0;
    const id = setInterval(() => {
      i++;
      setVal(Math.min(target, (target / steps) * i));
      if (i >= steps) clearInterval(id);
    }, stepMs);
    return () => clearInterval(id);
  }, [target, animate]);
  return <>{val.toFixed(decimals)}</>;
}

// ─── Scene wrapper ────────────────────────────────────────────────────────────

function Scene({ number, title, summary, active, done, children, order }: {
  number: number; title: string; summary: string; active: boolean; done: boolean; children: React.ReactNode; order?: number;
}) {
  return (
    <div
      className={`rounded-xl border transition-all duration-500 ${
        active ? 'border-primary shadow-md' : done ? 'border-border' : 'border-border opacity-30'
      }`}
      style={order !== undefined ? { order } : undefined}
    >
      <div className="px-5 py-4 flex items-center gap-3">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
          active ? 'bg-primary text-primary-foreground' :
          done ? 'bg-emerald-500/15 text-emerald-600' :
          'bg-muted text-muted-foreground'
        }`}>{number}</span>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${active ? 'text-foreground' : 'text-muted-foreground'}`}>{title}</p>
          {(done || active) && <p className="text-xs text-muted-foreground mt-0.5">{summary}</p>}
        </div>
      </div>
      {active && (
        <div className="px-5 pb-5 border-t border-border pt-4 space-y-4">{children}</div>
      )}
    </div>
  );
}

// ─── Progress line ────────────────────────────────────────────────────────────

function ProgressLine({ scene, onBack, onContinue, onReplay }: {
  scene: number;
  onBack: () => void;
  onContinue: () => void;
  onReplay: () => void;
}) {
  const done = scene >= TOTAL_SCENES;
  return (
    <div className="flex items-center gap-1.5">
      {/* Back */}
      <button
        onClick={onBack}
        disabled={scene <= 1}
        title="Previous step"
        className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-25 transition-colors flex-shrink-0 mr-1"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
      </button>

      {/* Dots + connectors */}
      {Array.from({ length: TOTAL_SCENES }, (_, i) => i + 1).map((s) => {
        const isDone = s < scene;
        const isActive = s === scene;
        const isNext = s === scene + 1 && !done;
        return (
          <React.Fragment key={s}>
            {s > 1 && (
              <div className={`h-px w-3 flex-shrink-0 ${isDone || isActive ? 'bg-emerald-500' : 'bg-border'}`} />
            )}
            {isNext ? (
              <button
                onClick={onContinue}
                title={`Continue to ${SCENE_LABELS[s - 1]}`}
                className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 hover:opacity-90 transition-opacity ring-2 ring-primary/30"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <div
                title={SCENE_LABELS[s - 1]}
                className={`flex-shrink-0 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                  isActive
                    ? 'w-7 h-7 text-[10px] bg-primary text-primary-foreground'
                    : isDone
                    ? 'w-5 h-5 text-[9px] bg-emerald-500 text-white'
                    : 'w-5 h-5 text-[9px] bg-muted text-muted-foreground'
                }`}
              >
                {s}
              </div>
            )}
          </React.Fragment>
        );
      })}

      {/* Replay when all done */}
      {done && (
        <button
          onClick={onReplay}
          className="ml-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-muted transition-colors flex-shrink-0"
        >
          <RotateCcw className="w-3 h-3" /> Replay
        </button>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function BSSInspector() {
  // Org picker state
  const [allOrgs, setAllOrgs] = useState<Org[]>([]);
  const [orgQuery, setOrgQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Org | null>(null);
  const orgInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Guardrail rules — keyed by code, used to show live condition text
  const [guardrailRules, setGuardrailRules] = useState<Record<string, string>>({});

  useEffect(() => {
    supabase
      .from('guardrail_rules')
      .select('code, evaluation_logic')
      .eq('is_active', true)
      .then(({ data }) => {
        if (!data) return;
        const map: Record<string, string> = {};
        for (const r of data as Array<{ code: string; evaluation_logic: any }>) {
          map[r.code] = r.evaluation_logic?.message ?? r.evaluation_logic?.type ?? r.code;
        }
        setGuardrailRules(map);
      });
  }, []);

  // Calculation state
  const [loading, setLoading] = useState(false);
  const [calc, setCalc] = useState<Calculation | null>(null);
  // The org's stored Prime Score result, when it has one. Read as the signed-in super admin,
  // which `get_roadmap_result` allows for any org; null for an org with nothing computed.
  const [primeResult, setPrimeResult] = useState<RoadmapResult | null>(null);
  const [noCalc, setNoCalc] = useState(false);
  const [questionRules, setQuestionRules] = useState<QuestionRulesMap>({});

  // Scene state (0 = not started, 1–8 = current scene, 9 = done)
  const [scene, setScene] = useState(0);
  const [animate, setAnimate] = useState(false);

  // Load all orgs on mount
  useEffect(() => {
    supabase
      .from('organizations')
      .select('id, name')
      .order('name')
      .limit(500)
      .then(({ data }) => setAllOrgs((data as Org[]) || []));
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          orgInputRef.current && !orgInputRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredOrgs = orgQuery
    ? allOrgs.filter((o) => o.name.toLowerCase().includes(orgQuery.toLowerCase()))
    : allOrgs;

  const selectOrg = (org: Org) => {
    setSelectedOrg(org);
    setOrgQuery(org.name);
    setShowDropdown(false);
    setCalc(null);
    setPrimeResult(null);
    setNoCalc(false);
    setScene(0);
  };

  const resetOrg = () => {
    setSelectedOrg(null);
    setOrgQuery('');
    setCalc(null);
    setPrimeResult(null);
    setNoCalc(false);
    setScene(0);
    setTimeout(() => orgInputRef.current?.focus(), 50);
  };

  // Fetch latest calculation + question scoring rules
  const fetchCalc = useCallback(async () => {
    if (!selectedOrg) return;
    setLoading(true);
    setNoCalc(false);
    setScene(0);

    const [{ data }, { data: questions }, prime] = await Promise.all([
      supabase
        .from('bss_calculations')
        .select('*')
        .eq('organization_id', selectedOrg.id)
        .eq('is_latest', true)
        .single(),
      supabase
        .from('diagnostic_questions')
        .select('code, scoring_rules')
        .eq('type', 'numeric')
        .is('effective_until', null)
        .eq('is_active', true),
      // Never fail the inspection over the Prime Score read: an org with no result, or a
      // refusal, just means no Prime block.
      fetchRoadmapResult(selectedOrg.id).catch(() => null),
    ]);

    setPrimeResult(prime);
    setLoading(false);
    if (!data) { setNoCalc(true); return; }
    setCalc(data as Calculation);

    const rulesMap: QuestionRulesMap = {};
    for (const q of (questions ?? []) as Array<{ code: string; scoring_rules: { ranges?: ScoringRange[] } }>) {
      if (q.scoring_rules?.ranges?.length) rulesMap[q.code] = q.scoring_rules.ranges;
    }
    setQuestionRules(rulesMap);

    setScene(1);
    setAnimate(true);
  }, [selectedOrg]);

  // Auto-fetch when org is selected
  useEffect(() => {
    if (selectedOrg) fetchCalc();
  }, [fetchCalc]);

  const advance = useCallback(() => {
    setAnimate(true);
    setScene((s) => Math.min(s + 1, TOTAL_SCENES));
  }, []);

  const goBack = useCallback(() => {
    setAnimate(false);
    setScene((s) => Math.max(s - 1, 1));
  }, []);

  const replay = () => { setScene(1); setAnimate(true); };

  const recalcMutation = useRecalculateBSS();

  const handleRecalc = useCallback(async () => {
    if (!selectedOrg || !calc) return;
    recalcMutation.mutate(
      { sessionId: calc.session_id, organizationId: selectedOrg.id },
      { onSuccess: () => fetchCalc() },
    );
  }, [selectedOrg, calc, recalcMutation, fetchCalc]);

  const trace = calc?.calculation_trace;
  const bssScore = parseFloat(calc?.bss_score || '0');
  const adjScore = parseFloat(calc?.adjusted_bss_score || '0');
  const trendMod = parseFloat(calc?.trend_modifier || '0');

  return (
    <div className="flex flex-col h-full">
      {/* ── Fixed header ── */}
      <div className="flex-shrink-0 bg-card border-b border-border px-6 py-3 flex items-center gap-6">
        <div className="flex-shrink-0">
          <h1 className="text-base font-bold leading-tight">BSS Inspector</h1>
          <p className="text-[11px] text-muted-foreground">Animated pipeline replay</p>
        </div>

        {/* Progress line — only when running */}
        {scene >= 1 && (
          <div className="flex-1 flex justify-center">
            <ProgressLine
              scene={scene}
              onBack={goBack}
              onContinue={advance}
              onReplay={replay}
            />
          </div>
        )}
      </div>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4 max-w-4xl w-full">

        {/* Org picker */}
        <div className="flex gap-3 items-end">
          <div className="flex-1 relative">
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">Organisation</label>
            <div className="relative">
              <input
                ref={orgInputRef}
                value={orgQuery}
                onChange={(e) => { setOrgQuery(e.target.value); setSelectedOrg(null); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                placeholder={allOrgs.length ? `Search ${allOrgs.length} organisations…` : 'Loading…'}
                className="w-full pl-3 pr-8 py-2 text-sm rounded-lg border border-border bg-background outline-none focus:border-primary transition-colors"
              />
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            </div>

            {showDropdown && filteredOrgs.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-20 overflow-hidden max-h-56 overflow-y-auto"
              >
                {filteredOrgs.slice(0, 50).map((org) => (
                  <button
                    key={org.id}
                    onMouseDown={() => selectOrg(org)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                  >
                    {org.name}
                  </button>
                ))}
                {filteredOrgs.length > 50 && (
                  <p className="px-3 py-2 text-xs text-muted-foreground border-t border-border">
                    {filteredOrgs.length - 50} more — type to filter
                  </p>
                )}
              </div>
            )}
          </div>

          {(selectedOrg || calc) && (
            <button
              onClick={resetOrg}
              className="px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted transition-colors flex-shrink-0"
            >
              ← Back
            </button>
          )}
          {calc && (
            <button
              onClick={handleRecalc}
              disabled={recalcMutation.isPending}
              className="px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted transition-colors flex-shrink-0 flex items-center gap-1.5 disabled:opacity-50"
            >
              {recalcMutation.isPending
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <RefreshCw className="w-3.5 h-3.5" />}
              Recalculate
            </button>
          )}
        </div>

        {/* ── Loading: retrieve scores card with blur overlay ── */}
        {loading && (
          <div className="relative mt-2">
            <div className="filter blur-sm opacity-20 pointer-events-none space-y-4 select-none">
              <div className="rounded-xl border border-border bg-card h-24" />
              <div className="rounded-xl border border-border bg-card h-16" />
              <div className="rounded-xl border border-border bg-card h-16" />
              <div className="rounded-xl border border-border bg-card h-16" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="rounded-xl border border-border bg-card/90 backdrop-blur-sm px-8 py-6 flex flex-col items-center gap-3 shadow-lg">
                <Gauge className="w-9 h-9 text-primary" />
                <p className="text-sm font-semibold">Retrieving Scores</p>
                <p className="text-xs text-muted-foreground">Loading latest calculation…</p>
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mt-1" />
              </div>
            </div>
          </div>
        )}

        {/* ── No calc state ── */}
        {noCalc && !loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="rounded-full bg-muted p-4">
              <Gauge className="w-8 h-8 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold">No Calculation Found</p>
              <p className="text-xs text-muted-foreground mt-1">
                {selectedOrg?.name} hasn't completed a diagnostic session yet.
              </p>
            </div>
            <button
              onClick={resetOrg}
              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
            >
              Choose a different organisation
            </button>
          </div>
        )}

        {/* ── Idle: org selected, not yet inspected ── */}
        {/* ── Prime Score: the stored roadmap result, the number every screen now shows ── */}
        {primeResult && !loading && (
          <div className="rounded-xl border border-primary/30 bg-primary/5 px-5 py-4 space-y-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Prime Score</p>
              <p className="text-[11px] text-muted-foreground">
                Computed {formatDateTimeShort(primeResult.computedAt)}
              </p>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold">{Math.round(primeResult.score.bpsTotal)}</span>
              <span className="text-sm text-muted-foreground">/ {primeResult.score.bpsMax}</span>
              <span className="text-sm font-semibold">
                {lifecycleLabelFor(primeResult.score.lifecycleStage)}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {primeResult.pillars.map((p) => (
                <div key={p.pillar} className="rounded-lg border border-border bg-card px-2 py-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{p.label}</p>
                  <p className="text-sm font-semibold tabular-nums">{Math.round(p.pillarScore)}%</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Diagnostic score banner (0 to 100) ── */}
        {calc && scene >= 1 && (
          <div className="rounded-xl border border-border bg-card px-5 py-4 grid grid-cols-5 gap-4 animate-in fade-in duration-500">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Raw BSS</p>
              <p className="text-2xl font-bold mt-0.5"><CountUp target={bssScore} animate={animate && scene === 1} /></p>
            </div>
            <div className="flex items-center justify-center text-muted-foreground text-lg">+</div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Trend Adj</p>
              <p className={`text-2xl font-bold mt-0.5 ${trendMod > 0 ? 'text-emerald-500' : trendMod < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                {trendMod > 0 ? '+' : ''}{trendMod}
              </p>
            </div>
            <div className="flex items-center justify-center text-muted-foreground text-lg">=</div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Adjusted</p>
              <p className="text-2xl font-bold mt-0.5"><CountUp target={adjScore} animate={animate && scene === 1} /></p>
            </div>
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Stage</p>
              <p className="text-sm font-semibold capitalize mt-0.5">{calc.lifecycle_stage} · {calc.lifecycle_zone}</p>
            </div>
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Guardrails</p>
              <p className={`text-sm font-semibold mt-0.5 ${calc.guardrail_violations?.length > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                {calc.guardrail_violations?.length > 0 ? `${calc.guardrail_violations.length} fired` : 'all clear'}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Duration</p>
              <p className="text-sm font-semibold mt-0.5">{trace?.durationMs ?? '—'}ms</p>
            </div>
          </div>
        )}

        {/* ── No-trace notice ── */}
        {calc && !trace && scene >= 1 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-5 py-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-amber-700">Pre-trace calculation</p>
              <p className="text-xs text-amber-600 mt-0.5">This result predates the trace system. Recalculate to capture the full pipeline replay.</p>
            </div>
            <button
              onClick={handleRecalc}
              disabled={recalcMutation.isPending}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              {recalcMutation.isPending
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <RefreshCw className="w-3.5 h-3.5" />}
              Recalculate
            </button>
          </div>
        )}

        {/* ── Scenes: active first, trail below ── */}
        <div className="flex flex-col gap-4">

        {/* ── Scene 1: Responses Loaded ── */}
        {scene >= 1 && (
          <Scene
            number={1} title="Responses Loaded"
            order={scene === 1 ? 0 : scene - 1}
            summary={trace ? `${trace.steps.responsesLoaded?.totalResponses ?? 0} answers pulled from the session` : `Session: ${calc?.session_id?.slice(0, 8)}…`}
            active={scene === 1} done={scene > 1}
          >
            {trace?.steps.responsesLoaded ? (
              <div className="rounded-lg border border-border overflow-hidden text-xs">
                <div className="grid grid-cols-5 bg-muted/50 px-3 py-2 font-medium text-muted-foreground">
                  <span>Code</span><span>Pillar</span><span>Type</span><span>Answer</span><span>IDK?</span>
                </div>
                <div className="divide-y divide-border max-h-64 overflow-y-auto">
                  {trace.steps.responsesLoaded.responses.map((r, i) => (
                    <div key={i} className={`grid grid-cols-5 px-3 py-1.5 ${r.isIdk ? 'bg-amber-500/5' : ''}`}>
                      <span className="font-mono">{r.code}</span>
                      <span className="capitalize">{r.pillar}</span>
                      <span>{r.type}</span>
                      <span className="truncate">{r.answerValue ?? r.answerNumeric ?? '—'}</span>
                      <span>{r.isIdk ? <span className="text-amber-600">IDK</span> : '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No trace data — run a new diagnostic to capture responses.</p>
            )}
          </Scene>
        )}

        {/* ── Scene 2: Question Scoring ── */}
        {scene >= 2 && (
          <Scene
            number={2} title="Question Scoring"
            order={scene === 2 ? 0 : scene - 2}
            summary={trace?.steps.questionScores
              ? `${trace.steps.questionScores.filter((q) => q.rawScore !== null).length} scored · ${trace.steps.questionScores.filter((q) => q.rawScore === null).length} IDK excluded`
              : 'Each answer converted to 0–100'}
            active={scene === 2} done={scene > 2}
          >
            {trace?.steps.questionScores ? (
              <div className="rounded-lg border border-border overflow-hidden text-xs">
                <div className="grid grid-cols-[4rem_6rem_5rem_1fr_5rem] bg-muted/50 px-3 py-2 font-medium text-muted-foreground">
                  <span>Code</span><span>Pillar</span><span>Type</span><span>Formula</span><span className="text-right">Score</span>
                </div>
                <div className="divide-y divide-border max-h-72 overflow-y-auto">
                  {trace.steps.questionScores.map((q, i) => (
                    <div key={i} className={`grid grid-cols-[4rem_6rem_5rem_1fr_5rem] px-3 py-2 items-center gap-2 ${q.rawScore === null ? 'opacity-50' : ''}`}>
                      <span className="font-mono">{q.code}</span>
                      <span className="capitalize">{q.pillar}</span>
                      <span className="text-muted-foreground">{q.type}</span>
                      <span className="font-mono text-muted-foreground truncate flex items-center gap-0.5">
                        {q.formula}
                        {q.type === 'numeric' && questionRules[q.code] && (
                          <RangesTooltip ranges={questionRules[q.code]!} />
                        )}
                      </span>
                      <div className="space-y-1">
                        <span className={`block text-right font-medium ${scoreBg(q.rawScore ?? 0)}`}>
                          {q.rawScore === null ? '—' : Math.round(q.rawScore)}
                        </span>
                        {q.rawScore !== null && <ScoreBar value={q.rawScore} animate={animate && scene === 2} />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No trace data available.</p>
            )}
          </Scene>
        )}

        {/* ── Scene 3: Pillar Aggregation ── */}
        {scene >= 3 && (
          <Scene
            number={3} title="Pillar Aggregation"
            order={scene === 3 ? 0 : scene - 3}
            summary="Weighted average of answered questions per pillar"
            active={scene === 3} done={scene > 3}
          >
            <div className="grid grid-cols-2 gap-3">
              {PILLAR_ORDER.map((code) => {
                const p = trace?.steps.pillarAggregation?.find((x) => x.code === code);
                const score = p?.score ?? calc?.pillar_scores?.[code] ?? 0;
                return (
                  <div key={code} className="rounded-lg border border-border px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium capitalize">{code}</p>
                      <span className={`text-sm font-bold px-2 py-0.5 rounded-md ${scoreBg(score)}`}>
                        <CountUp target={score} animate={animate && scene === 3} />
                      </span>
                    </div>
                    <ScoreBar value={score} animate={animate && scene === 3} />
                    {p && (
                      <p className="text-[10px] text-muted-foreground">
                        {p.answeredCount}/{p.totalCount} answered · weight {p.pillarWeight.toFixed(3)}
                        {p.idkCount > 0 && ` · ${p.idkCount} IDK`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </Scene>
        )}

        {/* ── Scene 4: Raw BSS ── */}
        {scene >= 4 && (
          <Scene
            number={4} title="Raw BSS Calculation"
            order={scene === 4 ? 0 : scene - 4}
            summary={`Σ(pillar × weight) / Σ(weights) = ${bssScore.toFixed(1)}`}
            active={scene === 4} done={scene > 4}
          >
            {trace?.steps.rawBSS ? (
              <div className="space-y-3">
                <div className="rounded-lg border border-border overflow-hidden text-xs">
                  <div className="grid grid-cols-4 bg-muted/50 px-3 py-2 font-medium text-muted-foreground">
                    <span>Pillar</span><span className="text-right">Score</span><span className="text-right">Weight</span><span className="text-right">Contribution</span>
                  </div>
                  <div className="divide-y divide-border">
                    {trace.steps.rawBSS.contributions.map((c, i) => (
                      <div key={i} className="grid grid-cols-4 px-3 py-2">
                        <span className="capitalize">{c.code}</span>
                        <span className="text-right">{c.score.toFixed(1)}</span>
                        <span className="text-right">{c.weight.toFixed(3)}</span>
                        <span className="text-right font-medium">{c.contribution.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="grid grid-cols-4 px-3 py-2 bg-muted/30 font-medium">
                      <span>Total</span><span />
                      <span className="text-right">{trace.steps.rawBSS.weightTotal.toFixed(3)}</span>
                      <span className="text-right">{trace.steps.rawBSS.contributions.reduce((s, c) => s + c.contribution, 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg bg-primary/5 border border-primary/20 px-4 py-3 text-sm font-medium text-center">
                  Raw BSS = {trace.steps.rawBSS.contributions.reduce((s, c) => s + c.contribution, 0).toFixed(2)} / {trace.steps.rawBSS.weightTotal.toFixed(3)} = <span className="text-xl font-bold"><CountUp target={trace.steps.rawBSS.score} animate={animate && scene === 4} /></span>
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-primary/5 border border-primary/20 px-4 py-3 text-sm font-medium text-center">
                Raw BSS = <span className="text-xl font-bold"><CountUp target={bssScore} animate={animate && scene === 4} /></span>
              </div>
            )}
          </Scene>
        )}

        {/* ── Scene 5: Trend Modifier ── */}
        {scene >= 5 && (
          <Scene
            number={5} title="Trend Modifier"
            order={scene === 5 ? 0 : scene - 5}
            summary={`${trendMod > 0 ? '+' : ''}${trendMod} applied → Adjusted BSS = ${adjScore.toFixed(1)}`}
            active={scene === 5} done={scene > 5}
          >
            {trace?.steps.trendModifier ? (
              <div className="space-y-3">
                {trace.steps.trendModifier.trendQuestions.length > 0 ? (
                  <div className="rounded-lg border border-border overflow-hidden text-xs">
                    <div className="grid grid-cols-3 bg-muted/50 px-3 py-2 font-medium text-muted-foreground">
                      <span>Question</span><span className="text-right">Score</span><span className="text-right">Bar</span>
                    </div>
                    <div className="divide-y divide-border">
                      {trace.steps.trendModifier.trendQuestions.map((q, i) => (
                        <div key={i} className="grid grid-cols-3 px-3 py-2 items-center gap-2">
                          <span className="font-mono">{q.code}</span>
                          <span className="text-right">{q.score}</span>
                          <div className="w-24 ml-auto"><ScoreBar value={q.score} animate={animate && scene === 5} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No trend questions answered.</p>
                )}
                <div className="grid grid-cols-3 gap-3 text-center text-sm">
                  <div className="rounded-lg border border-border px-3 py-2">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Avg trend</p>
                    <p className="font-bold">{trace.steps.trendModifier.avgTrendScore.toFixed(1)}</p>
                  </div>
                  <div className="rounded-lg border border-border px-3 py-2">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Modifier</p>
                    <p className={`font-bold ${trendMod > 0 ? 'text-emerald-500' : trendMod < 0 ? 'text-red-500' : ''}`}>
                      {trendMod > 0 ? '+' : ''}{trace.steps.trendModifier.modifier}
                    </p>
                  </div>
                  <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Adjusted BSS</p>
                    <p className="font-bold text-primary"><CountUp target={trace.steps.trendModifier.adjustedBSS} animate={animate && scene === 5} /></p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">avg ≥ 80 → +5 · avg ≤ 40 → −5 · else → 0</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3 text-center text-sm">
                <div className="rounded-lg border border-border px-3 py-2">
                  <p className="text-[10px] text-muted-foreground uppercase">Modifier</p>
                  <p className="font-bold">{trendMod > 0 ? '+' : ''}{trendMod}</p>
                </div>
                <div className="col-span-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
                  <p className="text-[10px] text-muted-foreground uppercase">Adjusted BSS</p>
                  <p className="font-bold text-primary"><CountUp target={adjScore} animate={animate && scene === 5} /></p>
                </div>
              </div>
            )}
          </Scene>
        )}

        {/* ── Scene 6: Guardrails ── */}
        {scene >= 6 && (
          <Scene
            number={6} title="Guardrail Evaluation"
            order={scene === 6 ? 0 : scene - 6}
            summary={(() => {
              const violations = calc?.guardrail_violations || [];
              const total = trace?.steps.guardrailEvaluation?.length ?? violations.length;
              return violations.length > 0
                ? `${violations.length}/${total} rules fired — check below`
                : `All ${total} rules passed`;
            })()}
            active={scene === 6} done={scene > 6}
          >
            {trace?.steps.guardrailEvaluation ? (
              <div className="rounded-lg border border-border overflow-hidden text-xs">
                <div className="grid grid-cols-[1fr_2fr_6rem] bg-muted/50 px-3 py-2 font-medium text-muted-foreground">
                  <span>Rule</span><span>Condition</span><span className="text-center">Result</span>
                </div>
                <div className="divide-y divide-border">
                  {trace.steps.guardrailEvaluation.map((g, i) => (
                    <div key={i} className={`grid grid-cols-[1fr_2fr_6rem] px-3 py-2.5 items-center ${g.fired ? 'bg-red-500/5' : ''}`}>
                      <div>
                        <p className="font-medium">{g.name || g.code}</p>
                        <p className="text-muted-foreground text-[10px] mt-0.5 truncate">{g.code}</p>
                      </div>
                      <span className="font-mono text-muted-foreground">{guardrailRules[g.code] ?? g.logicSummary}</span>
                      <span className={`text-center font-medium ${g.fired ? 'text-red-500' : 'text-emerald-500'}`}>
                        {g.fired ? '⚡ FIRED' : '✓ PASS'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {(calc?.guardrail_violations?.length ?? 0) > 0 ? (
                  calc!.guardrail_violations.map((v) => (
                    <div key={v} className="flex items-center gap-3 rounded-lg bg-red-500/5 border border-red-500/20 px-3 py-2 text-sm">
                      <span className="text-red-500">⚡</span><span className="font-medium">{v}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-emerald-600">✓ No guardrail violations</p>
                )}
              </div>
            )}
          </Scene>
        )}

        {/* ── Scene 7: Lifecycle Stage ── */}
        {scene >= 7 && (
          <Scene
            number={7} title="Lifecycle Stage"
            order={scene === 7 ? 0 : scene - 7}
            summary={`→ ${calc?.lifecycle_stage} · ${calc?.lifecycle_zone}`}
            active={scene === 7} done={scene > 7}
          >
            <div className="space-y-4">
              <div className="rounded-xl border-2 border-primary bg-primary/5 px-6 py-5 text-center animate-in fade-in duration-700">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Lifecycle Stage</p>
                <p className="text-3xl font-bold capitalize">{calc?.lifecycle_stage}</p>
                <p className="text-sm text-muted-foreground mt-1">{calc?.lifecycle_zone} Zone · Adjusted BSS {adjScore.toFixed(1)}</p>
              </div>
              {trace?.steps.lifecycleStage && (
                <div className="space-y-2 text-xs">
                  <p className="font-medium text-muted-foreground">Selection reasoning</p>
                  <div className="rounded-lg bg-muted/30 px-3 py-2 font-mono">{trace.steps.lifecycleStage.reason}</div>
                  {trace.steps.lifecycleStage.candidateStages.length > 1 && (
                    <p className="text-muted-foreground">
                      Candidates in score range: {trace.steps.lifecycleStage.candidateStages.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </Scene>
        )}

        {/* ── Scene 8: Suggestions + Full Output ── */}
        {scene >= 8 && (
          <Scene
            number={8} title="Suggestions & Final Result"
            order={scene === 8 || scene === 9 ? 0 : scene - 8}
            summary={`${calc?.suggestions?.length ?? 0} suggestions · ${trace?.durationMs ?? '—'}ms total`}
            active={scene === 8 || scene === 9}
            done={false}
          >
            <div className="space-y-4">
              {(calc?.suggestions?.length ?? 0) > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Suggestions</p>
                  {calc!.suggestions.map((s, i) => (
                    <div key={i} className="rounded-lg border border-border px-4 py-3 flex items-start gap-3 animate-in fade-in duration-300" style={{ animationDelay: `${i * 100}ms` }}>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground mt-0.5 flex-shrink-0">{s.priority}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{s.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{s.description}</p>
                        <p className="text-[10px] font-mono text-muted-foreground/60 mt-1">{s.code}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Stored Result</p>
                <div className="rounded-lg border border-border overflow-hidden text-xs">
                  {([
                    ['bss_score', bssScore.toFixed(2)],
                    ['adjusted_bss_score', adjScore.toFixed(2)],
                    ['trend_modifier', calc?.trend_modifier],
                    ['decline_index', calc?.decline_index],
                    ['lifecycle_stage', calc?.lifecycle_stage],
                    ['lifecycle_zone', calc?.lifecycle_zone],
                    ['guardrail_violations', JSON.stringify(calc?.guardrail_violations)],
                    ['is_latest', String(calc?.is_latest)],
                    ['calculated_at', calc?.calculated_at?.slice(0, 19).replace('T', ' ')],
                    ['trace captured', calc?.calculation_trace ? 'yes' : 'no (pre-trace)'],
                    ['duration', trace ? `${trace.durationMs}ms` : '—'],
                  ] as [string, string | undefined][]).map(([k, v]) => (
                    <div key={k} className="grid grid-cols-2 px-3 py-1.5 border-b border-border last:border-0 hover:bg-muted/30">
                      <span className="font-mono text-muted-foreground">{k}</span>
                      <span className="font-mono truncate">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Scene>
        )}

        </div>{/* end scenes flex */}

        <div className="h-8" />
      </div>
    </div>
  );
}
